// Take-latest guards for the page's async reads.
//
// Three things make a flat key set wrong here:
//
//  1. Detail is fetched for more than one sub-ticket at a time — the expanded panel and the
//     label for a timer running elsewhere. A single `detail` counter means whichever lands
//     second wins for BOTH, so one reply can blank the other panel. Detail is therefore keyed
//     by task AND sub-ticket, and the two settle independently in either order.
//
//  2. The aside outlives the page. Its summary must survive the Sub Tasks route unmounting on
//     a tab switch, so generations are namespaced by owner and teardown invalidates only the
//     scope that actually went away.
//
//  3. Every read is per-Request, and a "newest generation" counter shared across Requests is
//     not enough on its own. A mutation on Request A can legitimately still be in flight when
//     the user has navigated to Request B: its refresh would bump the shared counter, mark the
//     list unsettled, and eventually apply A's rows under B. So keys are scoped BY TASK, and
//     on top of that each scope records which Request it is currently showing. A read is only
//     allowed to touch a store when it is both the newest of its own task key AND issued for
//     the Request that scope currently owns.
export type GenerationScope = 'page' | 'aside';

const generations: { [key: string]: number } = {};

// Which Request each scope is currently showing. Set by the component that owns the scope.
const owners: { [scope in GenerationScope]?: ID | null } = {};

export interface GenerationScoped {
    generation: number;
}

// Task-scoped read keys. A read for Request A cannot advance, or satisfy, Request B's counter.
export const listKey = (taskId: ID): string => `page:list:${taskId}`;
export const designerPickerKey = (taskId: ID): string => `page:designerPicker:${taskId}`;

// The open session is global to the DESIGNER, not to a Request, so it stays unscoped
export const ACTIVE_SESSION_KEY = 'page:activeSession';

// Aside-owned: never invalidated by page teardown, so an in-flight initial summary and any
// mutation-triggered summary refresh both survive a tab switch
export const summaryKey = (taskId: ID): string => `aside:summary:${taskId}`;

// Identity-keyed, so detail A and detail B cannot clobber one another
export const detailKey = (taskId: ID, subTicketId: ID): string => `page:detail:${taskId}:${subTicketId}`;

// Issue a request: returns the generation the caller must carry on its params
export const nextGeneration = (key: string): number => {
    generations[key] = (generations[key] ?? 0) + 1;

    return generations[key];
};

export const currentGeneration = (key: string): number => generations[key] ?? 0;

// True only for the newest in-flight request of that key; a late reply is silently dropped
export const isLatestGeneration = (key: string, generation: number): boolean =>
    generation === (generations[key] ?? 0);

// ------------------------------------------------------------------------- scope ownership
//
// Claimed when a scope starts showing a Request and released when it stops. Everything else is
// a guard against it: a read issued for a Request the scope no longer shows is not merely late,
// it is answering a question nobody is asking any more.
export const claimScope = (scope: GenerationScope, taskId: ID): void => {
    owners[scope] = taskId;
};

export const releaseScope = (scope: GenerationScope): void => {
    owners[scope] = null;
};

export const currentScopeOwner = (scope: GenerationScope): ID | null => owners[scope] ?? null;

export const isScopeOwner = (scope: GenerationScope, taskId: ID): boolean => owners[scope] === taskId;

// A read may touch its store only when it is the newest of its own task key AND that task is
// still the one its scope is showing. Both halves are load-bearing: the generation alone lets a
// stale Request's refresh through, and ownership alone lets an old read of the CURRENT Request
// overwrite a newer one.
export const isCurrentRead = (scope: GenerationScope, key: string, taskId: ID, generation: number): boolean =>
    isScopeOwner(scope, taskId) && isLatestGeneration(key, generation);

// Invalidates everything owned by one scope. Page teardown must not reach the aside's keys.
export const invalidateScope = (scope: GenerationScope): void => {
    const prefix = `${scope}:`;

    Object.keys(generations).forEach((key) => {
        if (key.startsWith(prefix)) {
            generations[key] += 1;
        }
    });
};

export const resetGenerations = (): void => {
    Object.keys(generations).forEach((key) => {
        delete generations[key];
    });

    (Object.keys(owners) as GenerationScope[]).forEach((scope) => {
        delete owners[scope];
    });
};
